const URLS_TO_CHECK = [
	{ url: 'https://anydesk.com', category: 'remote_desktop', name: 'AnyDesk' },
	{ url: 'https://teamviewer.com', category: 'remote_desktop', name: 'TeamViewer' },
	{ url: 'https://remotedesktop.google.com', category: 'remote_desktop', name: 'Chrome Remote Desktop' },
	{ url: 'https://rustdesk.com', category: 'remote_desktop', name: 'RustDesk' },
	{ url: 'https://parsec.app', category: 'remote_desktop', name: 'Parsec' },
	{ url: 'https://splashtop.com', category: 'remote_desktop', name: 'Splashtop' },
	{ url: 'https://connectwise.com', category: 'remote_desktop', name: 'ConnectWise Control' },
	{ url: 'https://logmein.com', category: 'remote_desktop', name: 'LogMeIn' },
	{ url: 'https://gotomypc.com', category: 'remote_desktop', name: 'GoToMyPC' },
	{ url: 'https://supremocontrol.com', category: 'remote_desktop', name: 'Supremo' },
	{ url: 'https://ultraviewer.net', category: 'remote_desktop', name: 'UltraViewer' },
	{ url: 'https://getscreen.me', category: 'remote_desktop', name: 'Getscreen.me' },
	{ url: 'https://ammyy.com', category: 'remote_desktop', name: 'Ammyy Admin' },
	{ url: 'https://dwservice.net', category: 'remote_desktop', name: 'DWService' },
	{ url: 'https://iperius.com', category: 'remote_desktop', name: 'Iperius Remote' },
	{ url: 'https://aweray.com', category: 'remote_desktop', name: 'AweRay / SunLogin' },
	{ url: 'https://oray.com', category: 'remote_desktop', name: 'SunLogin (CN)' },
	{ url: 'https://meshcentral.com', category: 'remote_desktop', name: 'MeshCentral' },
	{ url: 'https://realvnc.com', category: 'remote_desktop', name: 'RealVNC' },
	{ url: 'https://tightvnc.com', category: 'remote_desktop', name: 'TightVNC' },

	{ url: 'https://discord.com', category: 'video_call', name: 'Discord' },
	{ url: 'https://zoom.us', category: 'video_call', name: 'Zoom (.us)' },
	{ url: 'https://zoom.com', category: 'video_call', name: 'Zoom (.com)' },
	{ url: 'https://meet.google.com', category: 'video_call', name: 'Google Meet' },
	{ url: 'https://teams.microsoft.com', category: 'video_call', name: 'MS Teams' },
	{ url: 'https://teams.live.com', category: 'video_call', name: 'MS Teams (Live)' },
	{ url: 'https://skype.com', category: 'video_call', name: 'Skype' },
	{ url: 'https://webex.com', category: 'video_call', name: 'Webex' },
	{ url: 'https://whereby.com', category: 'video_call', name: 'Whereby' },
	{ url: 'https://jitsi.org', category: 'video_call', name: 'Jitsi' },
	{ url: 'https://meet.jit.si', category: 'video_call', name: 'Jitsi Meet' },
	{ url: 'https://slack.com', category: 'video_call', name: 'Slack' },
	{ url: 'https://telegram.org', category: 'video_call', name: 'Telegram' },
	{ url: 'https://web.telegram.org', category: 'video_call', name: 'Telegram Web' },

	{ url: 'https://twitch.tv', category: 'streaming', name: 'Twitch' },
	{ url: 'https://kick.com', category: 'streaming', name: 'Kick' },
	{ url: 'https://streamyard.com', category: 'streaming', name: 'StreamYard' },
	{ url: 'https://restream.io', category: 'streaming', name: 'Restream' },
	{ url: 'https://vdo.ninja', category: 'streaming', name: 'VDO.Ninja' },

	{ url: 'https://drive.google.com', category: 'file_sharing', name: 'Google Drive' },
	{ url: 'https://dropbox.com', category: 'file_sharing', name: 'Dropbox' },
	{ url: 'https://wetransfer.com', category: 'file_sharing', name: 'WeTransfer' },
] as const satisfies readonly UrlEntry[];

const BATCH_SIZE = 3;
const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 500;
const REACHABLE_THRESHOLD = 10;

function chunk<T>(arr: readonly T[], size: number): T[][] {
	const chunks: T[][] = [];
	for (let i = 0; i < arr.length; i += size) {
		chunks.push(arr.slice(i, i + size));
	}
	return chunks;
}

function delay(ms: number): Promise<void> {
	return new Promise((resolve) => setTimeout(resolve, ms));
}

function checkUrlOnce(entry: UrlEntry, timeoutMs: number): Promise<UrlCheckResult> {
	return new Promise((resolve) => {
		const controller = new AbortController();

		const timer = setTimeout(() => {
			controller.abort();
			resolve({
				...entry,
				reachable: false,
				status: null,
				error: 'TIMEOUT',
			});
		}, timeoutMs);

		fetch(`${entry.url}/favicon.ico?_cb=${Date.now()}`, {
			mode: 'no-cors',
			cache: 'no-store',
			signal: controller.signal,
		})
			.then(() => {
				clearTimeout(timer);
				resolve({
					...entry,
					reachable: true,
					status: null,
					error: null,
				});
			})
			.catch((err) => {
				clearTimeout(timer);
				if (err.name === 'AbortError') return;
				resolve({
					...entry,
					reachable: false,
					status: null,
					error: 'NETWORK_ERROR',
				});
			});
	});
}

/**
 * Check a single URL with retry logic.
 * - If reachable on any attempt → return immediately.
 * - If unreachable → retry up to MAX_RETRIES times with progressive backoff.
 */
async function checkUrlWithRetry(
	entry: UrlEntry,
	timeoutMs: number,
): Promise<UrlCheckResult> {
	let lastResult: UrlCheckResult | null = null;

	// eslint-disable-next-line no-restricted-syntax
	for (let attempt = 0; attempt <= MAX_RETRIES; attempt += 1) {
		if (attempt > 0) {
			// eslint-disable-next-line no-await-in-loop
			await delay(RETRY_DELAY_MS * attempt);
		}

		// eslint-disable-next-line no-await-in-loop
		const result = await checkUrlOnce(entry, timeoutMs);

		if (result.reachable) {
			return result;
		}

		lastResult = result;
	}

	return lastResult!;
}

/**
 * Process all URLs in batches of BATCH_SIZE (3 concurrent).
 * Each URL gets up to MAX_RETRIES (2) retries if it fails.
 */
async function checkAllUrls(timeoutMs: number): Promise<UrlCheckResult[]> {
	const batches = chunk(URLS_TO_CHECK, BATCH_SIZE);
	const allResults: UrlCheckResult[] = [];

	// eslint-disable-next-line no-restricted-syntax
	for (const batch of batches) {
		// eslint-disable-next-line no-await-in-loop
		const batchResults = await Promise.all(
			batch.map((entry) => checkUrlWithRetry(entry, timeoutMs)),
		);
		allResults.push(...batchResults);
	}

	return allResults;
}

/**
 * Status logic:
 *   0 reachable       → FIREWALL_ACTIVE        (all blocked, firewall working)
 *   1–10 reachable    → FIREWALL_INCOMPLETE     (partially blocked)
 *   >10 reachable     → FIREWALL_NOT_DETECTED   (no meaningful firewall)
 */
export async function checkFirewallStatus(
	timeoutMs = 5000,
): Promise<FirewallCheckResponse> {
	const results = await checkAllUrls(timeoutMs);

	const reachableThreats = results.filter((r) => r.reachable);
	const blockedThreats = results.filter((r) => !r.reachable);

	let status: FirewallStatus;
	if (reachableThreats.length === 0) {
		status = 'FIREWALL_ACTIVE';
	} else if (reachableThreats.length <= REACHABLE_THRESHOLD) {
		status = 'FIREWALL_INCOMPLETE';
	} else {
		status = 'FIREWALL_NOT_DETECTED';
	}

	const byCategory: FirewallCheckResponse['byCategory'] = {};
	// eslint-disable-next-line no-restricted-syntax
	for (const r of results) {
		const cat = r.category;
		if (!byCategory[cat]) byCategory[cat] = [];
		byCategory[cat]!.push({
			name: r.name,
			url: r.url,
			reachable: r.reachable,
			status: r.status,
			error: r.error,
		});
	}

	return {
		status,
		summary: {
			total: results.length,
			blocked: blockedThreats.length,
			reachable: reachableThreats.length,
		},
		reachableThreats: reachableThreats.map((r) => ({
			name: r.name,
			url: r.url,
			category: r.category,
		})),
		byCategory,
	};
}
